import React, { useState } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import CircularProgress from '@mui/material/CircularProgress'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import AddIcon from '@mui/icons-material/Add'
import { DndContext, DragEndEvent, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core'
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers'
import { TppSection, TppRow } from '../../types'
import SectionListItem from './SectionListItem'
import DeleteConfirmDialog from './DeleteConfirmDialog'

interface TppAdminSettingsModalProps {
    open: boolean
    onClose: () => void
    sections: TppSection[]
    rows: TppRow[]
    onCreateSection: (title: string) => Promise<void>
    onUpdateSectionTitle: (sectionId: string, title: string) => Promise<void>
    onDeleteSection: (sectionId: string) => Promise<void>
    onCreateRow: (sectionId: string, title: string) => Promise<void>
    onUpdateRowTitle: (rowId: string, title: string) => Promise<void>
    onDeleteRow: (rowId: string) => Promise<void>
    onReorderSections: (sectionIds: string[]) => void
    onReorderRows: (sectionId: string, rowIds: string[]) => void
    isCreatingSection: boolean
    isUpdatingSection: boolean
    isDeletingSection: boolean
    isCreatingRow: boolean
    isUpdatingRow: boolean
    isDeletingRow: boolean
}

const TppAdminSettingsModal = ({
    open,
    onClose,
    sections,
    rows,
    onCreateSection,
    onUpdateSectionTitle,
    onDeleteSection,
    onCreateRow,
    onUpdateRowTitle,
    onDeleteRow,
    onReorderSections,
    onReorderRows,
    isCreatingSection,
    isUpdatingSection,
    isDeletingSection,
    isCreatingRow,
    isUpdatingRow,
    isDeletingRow,
}: TppAdminSettingsModalProps) => {
    const [newSectionTitle, setNewSectionTitle] = useState('')
    const [showNewSectionInput, setShowNewSectionInput] = useState(false)
    const [deleteTarget, setDeleteTarget] = useState<{ type: 'section' | 'row'; item: TppSection | TppRow } | null>(
        null
    )
    const [addingRowToSection, setAddingRowToSection] = useState<string | null>(null)
    const [newRowTitle, setNewRowTitle] = useState('')

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

    const handleSectionDragEnd = (event: DragEndEvent) => {
        const { active, over } = event
        if (!over || active.id === over.id) return
        const ids = sections.map((s) => s._id)
        const oldIndex = ids.indexOf(active.id as string)
        const newIndex = ids.indexOf(over.id as string)
        if (oldIndex === -1 || newIndex === -1) return
        onReorderSections(arrayMove(ids, oldIndex, newIndex))
    }

    const handleCreateSection = async () => {
        if (newSectionTitle.trim()) {
            await onCreateSection(newSectionTitle.trim())
            setNewSectionTitle('')
            setShowNewSectionInput(false)
        }
    }

    const handleAddRow = (sectionId: string) => {
        setAddingRowToSection(sectionId)
        setNewRowTitle('')
    }

    const handleCreateRow = async () => {
        if (addingRowToSection && newRowTitle.trim()) {
            await onCreateRow(addingRowToSection, newRowTitle.trim())
            setNewRowTitle('')
            setAddingRowToSection(null)
        }
    }

    const handleConfirmDelete = async () => {
        if (!deleteTarget) return

        if (deleteTarget.type === 'section') {
            await onDeleteSection(deleteTarget.item._id)
        } else {
            await onDeleteRow(deleteTarget.item._id)
        }
        setDeleteTarget(null)
    }

    const isDeleting = isDeletingSection || isDeletingRow

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle sx={{ pb: 1 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="18x600">Board Settings</Typography>
                        <IconButton aria-label="close" onClick={onClose} sx={{ color: 'figma.grayscale.tier1' }}>
                            <HighlightOffIcon />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <Stack gap={2} py={1}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="16x600" color="figma.grayscale.textHier1">
                                Sections & Rows
                            </Typography>
                            {!showNewSectionInput && (
                                <Button
                                    startIcon={<AddIcon />}
                                    onClick={() => setShowNewSectionInput(true)}
                                    sx={{ color: 'figma.primary' }}
                                >
                                    Add Section
                                </Button>
                            )}
                        </Stack>

                        {showNewSectionInput && (
                            <Stack direction="row" gap={1} alignItems="center">
                                <TextField
                                    size="small"
                                    placeholder="New section title"
                                    value={newSectionTitle}
                                    onChange={(e) => setNewSectionTitle(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleCreateSection()
                                        if (e.key === 'Escape') {
                                            setShowNewSectionInput(false)
                                            setNewSectionTitle('')
                                        }
                                    }}
                                    disabled={isCreatingSection}
                                    autoFocus
                                    sx={{ flex: 1 }}
                                />
                                <Button
                                    variant="contained"
                                    onClick={handleCreateSection}
                                    disabled={isCreatingSection || !newSectionTitle.trim()}
                                    sx={{ borderRadius: '14px' }}
                                >
                                    {isCreatingSection ? <CircularProgress size={20} /> : 'Add'}
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        setShowNewSectionInput(false)
                                        setNewSectionTitle('')
                                    }}
                                    disabled={isCreatingSection}
                                    sx={{ borderRadius: '14px' }}
                                >
                                    Cancel
                                </Button>
                            </Stack>
                        )}

                        <Stack gap={1.5} sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                                onDragEnd={handleSectionDragEnd}
                            >
                                <SortableContext
                                    items={sections.map((s) => s._id)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    {sections.map((section) => (
                                        <React.Fragment key={section._id}>
                                            <SectionListItem
                                                section={section}
                                                rows={rows}
                                                onUpdateSectionTitle={onUpdateSectionTitle}
                                                onDeleteSection={(s) => setDeleteTarget({ type: 'section', item: s })}
                                                onAddRow={handleAddRow}
                                                onUpdateRowTitle={onUpdateRowTitle}
                                                onDeleteRow={(r) => setDeleteTarget({ type: 'row', item: r })}
                                                onReorderRows={onReorderRows}
                                                isUpdatingSection={isUpdatingSection}
                                                isUpdatingRow={isUpdatingRow}
                                                isAddingRow={isCreatingRow && addingRowToSection === section._id}
                                            />
                                            {addingRowToSection === section._id && (
                                                <Stack direction="row" gap={1} alignItems="center" ml={4} px={2}>
                                                    <TextField
                                                        size="small"
                                                        placeholder="New row title"
                                                        value={newRowTitle}
                                                        onChange={(e) => setNewRowTitle(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') handleCreateRow()
                                                            if (e.key === 'Escape') {
                                                                setAddingRowToSection(null)
                                                                setNewRowTitle('')
                                                            }
                                                        }}
                                                        disabled={isCreatingRow}
                                                        autoFocus
                                                        sx={{ flex: 1 }}
                                                    />
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        onClick={handleCreateRow}
                                                        disabled={isCreatingRow || !newRowTitle.trim()}
                                                        sx={{ borderRadius: '14px' }}
                                                    >
                                                        {isCreatingRow ? <CircularProgress size={16} /> : 'Add'}
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={() => {
                                                            setAddingRowToSection(null)
                                                            setNewRowTitle('')
                                                        }}
                                                        disabled={isCreatingRow}
                                                        sx={{ borderRadius: '14px' }}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </Stack>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </SortableContext>
                            </DndContext>

                            {sections.length === 0 && (
                                <Typography
                                    variant="14x400"
                                    color="figma.grayscale.textHier3"
                                    textAlign="center"
                                    py={4}
                                >
                                    No sections yet. Click "Add Section" to create one.
                                </Typography>
                            )}
                        </Stack>
                    </Stack>
                </DialogContent>
            </Dialog>

            <DeleteConfirmDialog
                open={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleConfirmDelete}
                isLoading={isDeleting}
                title={deleteTarget?.type === 'section' ? 'Delete Section' : 'Delete Row'}
                itemName={deleteTarget?.item.title || ''}
                itemType={deleteTarget?.type || 'section'}
            />
        </>
    )
}

export default TppAdminSettingsModal
